import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  AppBar,
  Toolbar,
  InputBase,
  IconButton,
  Badge,
  Avatar,
  Menu as MuiMenu,
  MenuItem,
  Box,
  alpha,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search,
  Notifications,
  AccountCircle,
  Brightness4,
  Brightness7,
  ExitToApp,
  Menu,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { useDispatch } from 'react-redux';
import { toggleSidebar } from '../../store/slices/uiSlice';

const Navbar = ({ sidebarWidth, setCurrentPage, isMobile }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isDarkMode, toggleTheme } = useCustomTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [language, setLanguage] = useState('en');
  const [logoutOpen, setLogoutOpen] = useState(false);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    setCurrentPage('profile');
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    setLogoutOpen(true);
    handleMenuClose();
  };

  const handleLogoutConfirm = () => {
    setLogoutOpen(false);
    // Add logout logic here
    console.log('User logged out');
  };

  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
        ml: isMobile ? 0 : `${sidebarWidth}px`,
        background: theme.palette.mode === 'dark'
          ? 'rgba(15, 15, 35, 0.9)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(40px) saturate(180%)',
        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
        boxShadow: `0 4px 30px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
        borderBottom: `1px solid ${theme.palette.divider}40`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            edge="start"
            onClick={() => dispatch(toggleSidebar())}
            sx={{
              mr: 2,
              color: theme.palette.text.primary,
              background: `${theme.palette.primary.main}08`,
              '&:hover': {
                background: `${theme.palette.primary.main}15`,
                color: theme.palette.primary.main,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Menu />
          </IconButton>
        )}
        
        <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.divider}50`,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(255, 255, 255, 0.9)',
              border: `1px solid ${theme.palette.primary.main}30`,
              boxShadow: `0 4px 20px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
            },
            marginRight: theme.spacing(2),
            marginLeft: 0,
            width: '100%',
            maxWidth: isMobile ? 250 : 400,
            transition: 'all 0.2s ease',
          }}
        >
          <Box
            sx={{
              padding: theme.spacing(0, 2),
              height: '100%',
              position: 'absolute',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Search sx={{ color: theme.palette.text.secondary }} />
          </Box>
          <InputBase
            placeholder="Search..."
            sx={{
              color: theme.palette.text.primary,
              '& .MuiInputBase-input': {
                padding: theme.spacing(1, 1, 1, 0),
                paddingLeft: `calc(1em + ${theme.spacing(4)})`,
                transition: theme.transitions.create('width'),
                width: '100%',
              },
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {/* Language Switcher - Hidden on mobile */}
          {!isMobile && (
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={language}
                onChange={handleLanguageChange}
                sx={{
                  background: `${theme.palette.background.paper}80`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.divider}`,
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  },
                  '&:hover': {
                    background: `${theme.palette.background.paper}90`,
                  },
                }}
              >
                <MenuItem value="en">🇺🇸 EN</MenuItem>
                <MenuItem value="es">🇪🇸 ES</MenuItem>
                <MenuItem value="fr">🇫🇷 FR</MenuItem>
                <MenuItem value="de">🇩🇪 DE</MenuItem>
              </Select>
            </FormControl>
          )}

          <motion.div 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <IconButton
              size={isMobile ? 'medium' : 'large'}
              onClick={toggleTheme}
              sx={{ 
                color: theme.palette.text.primary,
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${theme.palette.divider}60`,
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(99, 102, 241, 0.15)'
                    : 'rgba(99, 102, 241, 0.1)',
                  color: theme.palette.primary.main,
                  borderColor: `${theme.palette.primary.main}60`,
                  boxShadow: `0 4px 20px ${theme.palette.primary.main}20`,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isDarkMode ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <IconButton
              size={isMobile ? 'medium' : 'large'}
              sx={{ 
                color: theme.palette.text.primary,
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${theme.palette.divider}60`,
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(236, 72, 153, 0.15)'
                    : 'rgba(236, 72, 153, 0.1)',
                  color: theme.palette.secondary.main,
                  borderColor: `${theme.palette.secondary.main}60`,
                  boxShadow: `0 4px 20px ${theme.palette.secondary.main}20`,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Badge 
                badgeContent={4} 
                sx={{
                  '& .MuiBadge-badge': {
                    background: theme.palette.error.main,
                    fontSize: '0.7rem',
                    minWidth: 16,
                    height: 16,
                  },
                }}
              >
                <Notifications />
              </Badge>
            </IconButton>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <IconButton
              size={isMobile ? 'medium' : 'large'}
              edge="end"
              onClick={handleProfileMenuOpen}
              sx={{ 
                color: theme.palette.text.primary,
                p: 0.5,
              }}
            >
              <Avatar
                sx={{
                  width: isMobile ? 32 : 36,
                  height: isMobile ? 32 : 36,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  border: `2px solid ${theme.palette.primary.main}25`,
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
                  fontWeight: 700,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}
              >
                A
              </Avatar>
            </IconButton>
          </motion.div>
        </Box>

        <MuiMenu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              background: theme.palette.background.paper,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              mt: 1.5,
            },
          }}
        >
          <MenuItem onClick={handleProfileClick}>
            <AccountCircle sx={{ mr: 1 }} /> Profile
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>My account</MenuItem>
          <MenuItem onClick={handleLogoutClick} sx={{ color: '#ef4444' }}>
            <ExitToApp sx={{ mr: 1 }} /> Logout
          </MenuItem>
        </MuiMenu>

        {/* Logout Confirmation Dialog */}
        <Dialog
          open={logoutOpen}
          onClose={() => setLogoutOpen(false)}
          PaperProps={{
            sx: {
              background: theme.palette.mode === 'dark' 
                ? `linear-gradient(135deg, rgba(30, 30, 60, 0.95), rgba(20, 20, 40, 0.9))`
                : `linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))`,
              backdropFilter: 'blur(30px)',
              border: `1px solid ${theme.palette.primary.main}30`,
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            <ExitToApp sx={{ fontSize: 48, color: '#ef4444', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Confirm Logout
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ textAlign: 'center', color: theme.palette.text.secondary }}>
              Are you sure you want to logout?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
            <Button 
              onClick={() => setLogoutOpen(false)}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLogoutConfirm}
              variant="contained"
              sx={{ 
                minWidth: 100,
                background: '#ef4444',
                '&:hover': { background: '#dc2626' }
              }}
            >
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;